/* eslint-disable max-len */
// MarketingManagement.page.tsx
import React, { useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import MarketingSkeletonAnimation from './MarketingSkeletonAnimation.page'
import { Button, ButtonGroup, Calendar } from '@components/common'
import { useMarketingCampaign } from './hooks/useMarketingManagement'
import './MarketingManagementStyle.css'
import { format } from 'date-fns'
import { AddCampaignModal } from '@components/layout/Modals'
import type { MarketingOutletContextValue } from './marketingOutletContext'
import { marketingOverviewFilter } from '@dataTypes/marketing.type'

const MarketingManagement: React.FC = () => {
  const hooks = useMarketingCampaign()
  const [showCampaignCalendarNav, setShowCampaignCalendarNav] = useState(false)

  useEffect(() => {
    if (hooks.activeTab !== 'campaign') {
      setShowCampaignCalendarNav(false)
    }
  }, [hooks.activeTab])

  const outletContext: MarketingOutletContextValue = {
    setShowCampaignCalendarNav,
    openAddCampaignModal: hooks.handleCreateCampaign,
  }

  return (
    <>
      <div
        className={`marketing-management-container${
          hooks.isPageReady ? ' is-ready' : ''
        }${
          hooks.showTemplatePreview && hooks.activeTab === 'templates'
            ? ' marketing-management-container--template-editor'
            : ''
        }`}
      >
        <div className="marketing-management-navbar">
          <div className="mmc-button-group-container">
            {hooks.showTemplatePreview && hooks.activeTab === 'templates' ? (
              <button
                type="button"
                onClick={hooks.handleBackToTemplates}
                className="unstyled-button mmn-back-template"
              >
                <i className="ti ti-chevron-left" aria-hidden />
                Back to templates
              </button>
            ) : (
              <ButtonGroup
                buttons={[
                  { label: 'Overview', value: 'overview' },
                  { label: 'Campaign', value: 'campaign' },
                  { label: 'Templates', value: 'templates' },
                ]}
                activeTab={hooks.activeTab}
                onButtonClick={hooks.handleChangeTab}
              />
            )}
            {hooks.activeTab === 'campaign' && showCampaignCalendarNav && (
              <>
                <span className="divider" />
                <div
                  id="mmc-campaign-calendar-nav-anchor"
                  className="mmc-campaign-calendar-nav-anchor"
                />
              </>
            )}
            {hooks.activeTab === 'overview' && (
              <>
                <span className="divider" />
                <div ref={hooks.calendarContainerRef}>
                  <Button
                    className="button-date button-secondary"
                    leftIcon={
                      <i className="button-date mgc_calendar_2_fill"></i>
                    }
                    rightIcon={
                      <i className="button-date mgc_down_small_fill"></i>
                    }
                    onClick={hooks.toggleCalendar}
                    size={'md'}
                  >
                    {format(new Date(hooks.selectedDate), 'MMMM d, yyyy')}
                  </Button>
                  {hooks.showCalendar && (
                    <Calendar
                      value={new Date(hooks.selectedDate)}
                      onChange={hooks.onDateChange}
                    />
                  )}
                </div>
                <div className="mmc-arrow-navigator">
                  <button
                    type="button"
                    className="unstyled-button ti ti-chevron-left"
                    aria-label="Previous day"
                    onClick={hooks.handlePrevDay}
                  />
                  <button
                    type="button"
                    className="unstyled-button ti ti-chevron-right"
                    aria-label="Next day"
                    onClick={hooks.handleNextDay}
                  />
                </div>
              </>
            )}
          </div>
          <div className="mmc-main-button-container">
            {hooks.activeTab === 'campaign' && (
              <div
                id="mmc-campaign-toolbar-anchor"
                className="mmc-campaign-toolbar-anchor"
              />
            )}
            {hooks.activeTab === 'overview' && (
              <ButtonGroup
                buttons={Object.values(marketingOverviewFilter).map(
                  (filter) => ({
                    label: filter,
                    value: filter,
                  }),
                )}
                activeTab={hooks.overviewDateFilter}
                onButtonClick={hooks.handleChangeFilter}
              />
            )}
            {hooks.showTemplatePreview && hooks.activeTab === 'templates' ? (
              hooks.isEmailTemplateEditor ? (
                <>
                  <Button
                    size="md"
                    className="button-secondary mm-cc-btn"
                    onClick={hooks.handlePreviewTemplate}
                  >
                    {hooks.templateEditorCanvasPreview ? 'Edit' : 'Preview'}
                  </Button>
                  <Button
                    size="md"
                    className="button-primary mm-cc-btn"
                    disabled={hooks.disableSaveTemplate}
                    onClick={hooks.handleSaveTemplate}
                  >
                    Save template
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="md"
                    className="button-secondary mm-cc-btn"
                    disabled={hooks.disableSaveTemplate}
                    onClick={hooks.handleSaveTemplate}
                  >
                    Save template
                  </Button>
                  <Button
                    size="md"
                    className="button-primary mm-cc-btn"
                    onClick={hooks.handleSendCampaignFromSmsTemplate}
                  >
                    Send campaign
                  </Button>
                </>
              )
            ) : (
              hooks.mainButtonText != null && (
                <Button
                  size="md"
                  className="button-primary mm-main-btn"
                  onClick={hooks.handleMainButtonClick}
                  disabled={
                    hooks.activeTab === 'campaign' && hooks.hasNoCampaigns
                  }
                >
                  {hooks.mainButtonText}
                </Button>
              )
            )}
            {/* Hide for the meantime, will uncomment once the functionality is finalized
            {hooks.activeTab === 'campaign' && (
              <Button
                size="md"
                className="button-secondary mm-settings-btn"
                onClick={hooks.onSettingsButtonClick}
              >
                <i className="mgc_settings_5_fill" />
              </Button>
            )} */}
          </div>
        </div>
        {hooks.isLoading ? (
          <MarketingSkeletonAnimation hideHeader />
        ) : (
          <Outlet context={outletContext} />
        )}
      </div>
      {hooks.showAddCampaignModal && (
        <AddCampaignModal
          key={hooks.addCampaignModalKey}
          show={hooks.showAddCampaignModal}
          onHide={hooks.onCampaignModalHide}
          initialCampaign={hooks.initialCampaign}
        />
      )}
    </>
  )
}

export default MarketingManagement
